import React, { FC, useContext, useEffect, useRef, useState } from "react";

import SelectBox from "../../../../../components/select-box/select-box";
import InputBox from "../../../../../components/input-box/input-box";
import MainButton from "../../../../../components/main-button";
import BodyText from "../../../../../components/components-text/body-text";
import LeftInfoModal from "../../../../../components/left-info-modal";
import Loader from "../../../../../components/loader";
import { MainTableContainer } from "../../../../../components/main-table/main-table-style";
import { ErrorModalContainer } from "../../../../../components/error-modal/error-modal-style";
import { LeftInfoModalProps } from "../../../../../components/left-info-modal/left-info-modal-layout";
import { RequiredDocDataContainer } from "../../../../../components/cross/required-documentation/required-doc.styles";
import { images } from "../../../../../assets";

import { DynamicProductsContext } from "../../../../../context/constants";
import { acceptedFileMimeType } from "../../../../../constants/acceptedFileMimeType";
import { SupplementsContext } from "../../../../../context/supplements/SupplementsContext";

import SupplementsServices from "../../../../../services/SupplementsServices";
import { deepGet, downloadFile } from '../../../../../utils/utility';
import { getFileSize, getFormatedDocs, getProductIdFromCollective, tableColumns } from "./utils";
import { getCollective } from "../../utils";
import { MODAL_CHANGE_DOC, MODAL_DELETE_DOC, MODAL_DOC_MAX_LIMIT, MODAL_GET_DOCS_ERROR } from "./helper";
import { Taker } from "../newInsuredStepper.types";
import { UseFormFunctionsProps } from "../../hooks/hooks.types";

const RequiredDocumentsStep: FC<UseFormFunctionsProps> = ({
  values,
  errors,
  handleChange,
  hideTitle = false,
  hideInsurance = false,
  title = "",
  setErrors,
  docNum,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const supplementsContext = useContext(SupplementsContext);

  const [actualForm, setActualForm] = useState<{type: string, description: string, file: {}}>({
    type: "",
    description: "",
    file: {},
  });
  const [uploadDocuments, setUploadDocuments] = useState<any[]>(deepGet(values, 'documents.uploadDocuments', []) || []);
  const [modal, setModal] = useState<Partial<LeftInfoModalProps> | null>(null);
  const [modalError, setModalError] = useState<{status: boolean, text: string}>({ status: false, text: "" });
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const taker: Taker = deepGet(values, 'taker') || { name: '', lastname1: '', lastname2: '', docNumber: ''};
  const takerCompleteName = `${taker.name} ${taker.lastname1} ${taker.lastname2}`;
  const associatesFilter = [{index:'0', name: takerCompleteName, document: taker.docNumber, title: `${takerCompleteName} (${taker.docNumber})`}]

  const context = useContext(DynamicProductsContext);
  const allProducts = deepGet(context, 'allProducts', {}) || {};

  useEffect(() => {
    getRequiredDocuments();
  }, []);

  const getRequiredDocuments = async () => {
    const { supplementId, supplementChildIds } = supplementsContext;
    const productId = getProductIdFromCollective(allProducts, getCollective());
    const response = await SupplementsServices.getRequiredDocuments({
      setLoading: () => {},
      loader: false,
      request: {
        supplementId,
        supplementChildIds: values.type === "MASCOTA" ? [-1] : supplementChildIds,
        productId,
        ...(docNum ? { idDoc: docNum } : ""),
      },
    });
    if (response.ok) {
      let formatedDocs = getFormatedDocs(response.data);
      if (formatedDocs.length > 0) {
        supplementsContext.setSupplementDocs(formatedDocs);
        setActualForm((prevValue) => ({
          ...prevValue,
          type: formatedDocs[0].value,
          description: formatedDocs[0].documentTypeMetadata,
        }));
      }
    } else {
      const modalContent = {
        ...MODAL_GET_DOCS_ERROR,
        mainText: response.message || MODAL_GET_DOCS_ERROR.mainText,
      };
      setModal(modalContent);
    }
    setIsLoading(false);
  }

  const handleOnChangeDocType = (value: string) => {
    const doc = supplementsContext.supplementDocs.find((doc) => doc.value === value);
    setActualForm((prevValue) => ({
      ...prevValue,
      type: value,
      description: doc ? doc.label : "",
    }));       
  }

  const onFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      setActualForm((prevValue) => ({...prevValue, file}));
    }
  };

  const attachDocument = (force: boolean = false) => {
    const hasDoc = uploadDocuments.find((doc) => doc.type === actualForm.type);
    if (hasDoc && !force) {
      const modalContent = {
        ...MODAL_CHANGE_DOC,
        onClickAccept: () => attachDocument(true),
      }
      setModal(modalContent);
      return;
    }

    const newUploadDocuments = force 
      ? [...uploadDocuments.filter((doc) => doc.type !== actualForm.type), actualForm]
      : [...uploadDocuments, actualForm];
      
    const filesSize = newUploadDocuments.reduce((totalSize: number, file: {file: {size: number}}) => totalSize + file.file.size, 0);
    if (filesSize >= (1048576 * 5)) {
      setModal(MODAL_DOC_MAX_LIMIT);
      return;
    } else {
      setErrors && setErrors({documents: null})
    }

    setUploadDocuments(newUploadDocuments);

    handleChange("uploadDocuments", newUploadDocuments, "documents");

    const defaultType = deepGet(supplementsContext,"supplementDocs.0.value", "") || "";
    const defaultDescription = deepGet(supplementsContext, "supplementDocs.0.label", "") || "";
    setActualForm({ type: defaultType, description: defaultDescription, file: {} });

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleOnClickDeleteDoc = (docIndex: number) => {
    const modalContent = {
      ...MODAL_DELETE_DOC,
      onClickAccept: () => deleteDocument(docIndex),
    }
    setModal(modalContent);
  };

  const deleteDocument = (docIndex: number) => {
    const newUploadsDocuments = uploadDocuments.filter((item, index) => index !== docIndex);
    setUploadDocuments(newUploadsDocuments);
    handleChange('uploadDocuments', newUploadsDocuments, 'documents');
  }

  const downloadDocument = (document: any) => {
    if (document) {
      downloadFile(document.file, document.file.name, document.file.type, false);
    } else {
      setModalError({status: true, text: 'Ha habido un error en la carga del fichero'})
    }
  }

  const getIsAttachButtonDisabled = () => {
    const hasFile = !!deepGet(actualForm, "file.name", false);
    const hasType = !!actualForm.type;
    return hasFile && hasType ? false : true;
  };

  return (
    <RequiredDocDataContainer>
      {isLoading && <Loader />}
      {!hideTitle && (
        <div className="data-flow-status">
          <div className="data-flow-status-title">
            <h2>{"DOCUMENTACION REQUERIDA"}</h2>
          </div>
        </div>
      )}

      <div className="content-wrapper bottomMargin20 filesSection">
        <div className="personal-data-wrapper">
          <div>
            <div className="data-title">
              <h5>ADJUNTAR DOCUMENTOS {title}</h5>
            </div>

            <div className="colSin-100 wrap">
              <div className={` ${!hideInsurance ? "colSin-50 rightPadding20" : "colSin-100" }`} >
                <div className="selectBox">
                  <SelectBox
                    optionsText={supplementsContext.supplementDocs as unknown as {[key:string]: string}[]}
                    optionKey={"value"}
                    optionValue={"label"}
                    defaultValue={actualForm.type}
                    initialValue={actualForm.type}
                    onChange={handleOnChangeDocType}
                  />
                  <label>Tipo de documento</label>
                </div>
              </div>

              {!hideInsurance && (
                <div className="colSin-50 rightPadding20">
                  <div className="selectBox">
                    <SelectBox
                      optionsText={associatesFilter}
                      optionKey={"document"}
                      optionValue={"title"}
                      defaultValue={deepGet(values, "taker.docNumber", "")}
                      initialValue={deepGet(values, "taker.docNumber", "")}
                      onChange={() => {}}
                      disabled={true}
                    />
                    <label>Asegurado</label>
                  </div>
                </div>
              )}
              {!hideInsurance && (
                <div className="colSin-100 rightPadding20">
                  <div className="inputBox custom-file-upload">
                    <div className="colSin-100">
                      <InputBox
                        type={"text"}
                        errorCode={""}
                        cleanValue={true}
                        value={actualForm.description}
                        onChange={(value: string) =>
                          setActualForm((prevValue) => ({
                            ...prevValue,
                            description: value,
                          }))
                        }
                      />
                    </div>
                    <label>Descripción</label>
                  </div>
                </div>
              )}
            </div>

            <div className="colSin-100 topMargin20">
              <div className="colSin-50 rightPadding20">
                <div className="inputBox custom-file-upload">
                  <div className="colSin-70">
                    <input
                      type="text"
                      readOnly
                      value={deepGet(actualForm, 'file.name', '')}
                    />
                    {errors && errors.documents && <p className="error">*Obligatorio adjuntar: {errors.documents}</p>}
                  </div>

                  <div className="colSin-30">
                    <label className="custom-file-upload-button topMargin5">
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={onFileChange}
                        accept={acceptedFileMimeType.toString()}
                      />
                      <MainButton
                        onClick={() => {
                          return true;
                        }}
                        text="Buscar"
                      />
                    </label>
                  </div>

                  <label>Adjuntar documento</label>
                </div>
              </div>

              <div className="colSin-50 rightPadding20 topMargin15">
                <label className="custom-file-upload">
                  <div className={"upload-buttons"}>
                    <MainButton
                      type={
                        getIsAttachButtonDisabled()
                          ? "disabled"
                          : ""
                      }
                      onClick={attachDocument}
                      text="Adjuntar Documento"
                      icon={true}
                      iconImg={images.IconUploadBlue}
                      disabled={getIsAttachButtonDisabled()}
                    />
                  </div>
                </label>
              </div>
            </div>
          </div>

          <div className="colSin-100 topMargin20">
            {uploadDocuments.length > 0 && (
              <>
                <MainTableContainer className="table noZebra uploadedTable">
                  <thead>
                    <tr>
                      {tableColumns.map((column, index) => (
                        <th key={`column-${index}`}>
                          <BodyText className="dimmed-mini-title">
                            <p>{column}</p>
                          </BodyText>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {uploadDocuments
                      .map(
                        (row, index) =>
                          !row.hidden && (
                            <tr className="main-table-row" key={`row-${index}`}>
                              <td>
                                <BodyText className="dimmed-mini-body">
                                  <p>{row.description}</p>
                                </BodyText>
                              </td>
                              <td>
                                <BodyText className="dimmed-mini-body">
                                  <p> {takerCompleteName}(Nuevo) </p>
                                </BodyText>
                              </td>
                              <td>
                                <div className="uploadedFileTable">
                                  <MainButton
                                    key={`downloadBtnTable${index}`}
                                    onClick={() => row && downloadDocument(row) }
                                    text=""
                                    icon={true}
                                    iconImg={images.IconEyewhite}
                                  />
                                  {!row.id && (
                                    <MainButton
                                      key={`deleteBtnTable${index}`}
                                      onClick={() => handleOnClickDeleteDoc(index)}
                                      text=""
                                      icon={true}
                                      iconImg={images.IconDeleteWhite}
                                    />
                                  )}
                                </div>
                              </td>
                              <td>
                                <BodyText className="dimmed-mini-body">
                                  <p>
                                    {row.size
                                      ? getFileSize(row.size)
                                      : row.file
                                      ? getFileSize(row.file.size)
                                      : ""}
                                  </p>
                                </BodyText>
                              </td>
                            </tr>
                          )
                      )
                      .reverse()}
                  </tbody>
                </MainTableContainer>
                <p className="text-max-size">
                  * El tamaño máximo de la totalidad de ficheros a adjuntar es
                  de 5MB.
                </p>
              </>
            )}
          </div>
        </div>
      </div>

      <ErrorModalContainer 
        className={`${modalError.status && `active`}`}
      >
        <div className="recover-modal">
          <div className="server-error">
            <p>{modalError.text}</p>
          </div>
          <div className="button-error">
            <MainButton text="Aceptar" onClick={() => setModalError({ status: false, text: ''})} />
          </div>
        </div>
      </ErrorModalContainer>

      {modal && (
        <LeftInfoModal
          active={modal.active}
          className={modal.className || ""}
          img={modal.img}
          mainTitle={modal.mainTitle}
          mainText={modal.mainText}
          secondaryText={modal.secondaryText || ""}
          buttonText={modal.buttonText}
          closeButtonText={modal.closeButtonText || ""}
          close={() => setModal(null)}
          onClickAccept={modal.onClickAccept || (() => {})}
          noMainButton={modal.noMainButton}
          centerPrimaryButton={modal.centerPrimaryButton}
          noSecondaryButton={modal.noSecondaryButton}
        />
      )}
    </RequiredDocDataContainer>
  );
};

export default RequiredDocumentsStep;
